
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useEffect } from "react";

import {
  fetchLikeStatus,
  toggleLike,
  fetchLikesByPost,
} from "../redux/thunks/likesThunks";

import {
  makeSelectLikeStatusForPost,
  selectLikesLoading,
  selectLikesError,
} from "../redux/selectors/likesSelectors";

const toKey = (v) => (v == null ? "" : String(v).trim());

const toId = (v) => {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return undefined;

  // si numérique => number, sinon string (ex: mongo ObjectId)
  const n = Number(s);
  if (Number.isFinite(n) && s !== "") return n;
  return s;
};

const getActorId = (user) =>
  user?.id ?? user?._id ?? user?.userId ?? user?.pgId ?? user?.uuid ?? null;

export default function useLikePost(postId, userId, postOwnerId) {
  const dispatch = useDispatch();

  const actorFromStore = useSelector((state) => {
    const user =
      state?.auth?.user ||
      state?.patient?.user ||
      state?.doctor?.user ||
      state?.user?.user ||
      state?.users?.currentUser ||
      state?.users?.user ||
      null;
    return getActorId(user);
  });

  // ✅ selector factory (stable)
  const selectLikeStatus = useMemo(makeSelectLikeStatusForPost, []);

  // ✅ postId toujours string non vide
  const pid = useMemo(() => {
    const k = toKey(postId);
    return k || null;
  }, [postId]);

  // ✅ userId et ownerId peuvent être int ou string
  const resolvedUserId = useMemo(
    () => toId(userId ?? actorFromStore),
    [userId, actorFromStore]
  );
  const resolvedOwnerId = useMemo(() => toId(postOwnerId), [postOwnerId]);

  // ✅ status local par postId
  const status = useSelector((state) =>
    pid ? selectLikeStatus(state, pid) : { liked: false, count: 0 }
  );

  const loading = useSelector(selectLikesLoading);
  const error = useSelector(selectLikesError);

  // (debug) aide à voir ce qui part au backend
  useEffect(() => {
  }, [pid, resolvedUserId, resolvedOwnerId, status]);

  /**
   * Status "liked" pour CE user
   * ✅ on skip si userId absent
   */
  const fetchStatus = useCallback(() => {
    if (!pid || resolvedUserId == null) return Promise.resolve(undefined);

    const payload = { postId: pid, userId: resolvedUserId };

    return dispatch(fetchLikeStatus(payload));
  }, [dispatch, pid, resolvedUserId]);

  /**
   * Toggle like
   * ✅ on dispatch TOUJOURS si postId existe
   * ✅ on inclut postOwnerId si dispo (important côté backend)
   */
  const handleToggleLike = useCallback(() => {
    if (!pid || resolvedUserId == null) return Promise.resolve(undefined);

    const payload = {
      postId: pid,
      ...(resolvedUserId != null ? { userId: resolvedUserId } : {}),
      ...(resolvedOwnerId != null ? { postOwnerId: resolvedOwnerId } : {}),
    };

    return dispatch(toggleLike(payload));
  }, [dispatch, pid, resolvedUserId, resolvedOwnerId]);

  /**
   * Likes agrégés (count) — optionnel
   */
  const fetchLikesAgg = useCallback(() => {
    if (!pid) return Promise.resolve(undefined);

    const payload = { postId: pid, _ts: Date.now() };

    return dispatch(fetchLikesByPost(payload));
  }, [dispatch, pid]);

  return {
    liked: !!status?.liked,
    count: status?.count ?? 0,
    loading,
    error,
    fetchStatus,
    handleToggleLike,
    fetchLikesAgg,
  };
}

